<template>
	<div class="form">
		<ValidationObserver
			v-slot="{ errors, invalid, handleSubmit }"
			@submit.prevent
		>
			<h4 class="step-title">
				<label for="phoneNumber">What's your phone number?</label>
			</h4>
			<div class="step-content d-flex flex-column align-items-center">
				<div class="step-item">
					<div class="form-group row">
						<div class="col-12 col-md-6 mx-auto">
							<ValidationProvider
								mode="eager"
								vid="phoneNumber"
								name="Phone number"
								rules="required|phone"
							>
								<input
									id="phoneNumber"
									class="form-control min-content-width"
									placeholder="(xxx) xxx-xxxx"
									type="tel"
									v-on:keyup.enter="enterKeyup"
									v-model="lead.phoneNumber"
								/>
							</ValidationProvider>

							<div
								class="alert alert-danger"
								v-if="
									errors['phoneNumber'] &&
									errors['phoneNumber'].length > 0
								"
							>
								{{ errors["phoneNumber"][0] }}
							</div>
						</div>
					</div>
				</div>
				<div class="step-item">
					<ValidationProvider
						mode="eager"
						vid="leadid_tcpa_disclosure"
						name="consent"
						v-bind:rules="{ required: { allowFalse: false } }"
					>
						<div class="d-flex">
							<div id="consent-wrapper">
								<input
									id="leadid_tcpa_disclosure"
									type="checkbox"
									v-model="checked"
								/>
							</div>
							<label id="tcpa-text" for="leadid_tcpa_disclosure">
								By clicking "View My Quote", I am providing my
								electronic signature and express written consent
								to receive telemarketing calls, text messages,
								emails, and postal mail from
								{{ websiteForConsent }}, this Website's
								marketing and re-marketing network, and up to
								eight insurance companies or their affiliates or
								representatives (who are identified
								<a v-bind:href="partnersUrl" target="_blank"
									>here</a
								>) at the phone number
								{{ phoneNumberForConsent }}, email address
								{{ lead.email }}, and postal address
								{{ addressForConsent }} provided by me. I
								consent to calls and text messages transmitting
								insurance quotes, or seeking related information
								from me, and I agree such contact may be made
								using an automatic telephone dialing system
								and/or an artificial or prerecorded voice
								messages.<br><br>.<br /><br />
								Additionally, by clicking "View My Quote", I
								acknowledge that I have read, understand, and
								agree to this Website's
								<a
									v-bind:href="privacyPolicyUrl"
									target="_blank"
									>Privacy Policy</a
								>
								and
								<a
									v-bind:href="termsAndConditionsUrl"
									target="_blank"
									>Terms of Use</a
								>.<br /><br />
								Finally, by providing my electronic signature, I
								acknowledge that giving my consent is not a
								condition of purchasing any property, goods, or
								services, and I may revoke my consent at any
								time.
							</label>
						</div>
					</ValidationProvider>
				</div>
				<div class="step-item">
					<div
						class="alert alert-danger"
						v-if="
							!submitted &&
							errors['leadid_tcpa_disclosure'] &&
							errors['leadid_tcpa_disclosure'].length > 0
						"
					>
						{{ errors["leadid_tcpa_disclosure"][0] }}
					</div>
				</div>
				<div class="step-item" v-if="!submitted && !appState.submitted">
					<button
						class="btn btn-step btn-lg form-control"
						ref="complete"
						v-on:click="handleSubmit(complete)"
					>
						View My Quote
					</button>
				</div>
				<div class="step-item" v-if="submitted && !appState.submitted">
					<div class="spinner-border text-light" role="status">
						<span class="sr-only">Loading...</span>
					</div>
				</div>
				<div class="step-item" v-if="appState.submitted">
					<div
						class="alert alert-success"
						role="alert"
						v-if="appState.submittedSuccessfully"
					>
						Thank You! Your information has successfully been
						submitted.
					</div>
					<div class="alert alert-danger" role="alert" v-else>
						Uh oh, there was a problem submitting your information.
						Please call us to get your quote.
					</div>
				</div>
			</div>
			<SlotInvalidInputWatchComponent
				v-bind:invalid="invalid && checked && !submitted"
				v-bind:value="lead.phoneNumber"
				v-on:invalidInput="onInvalidInput"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import { extend } from "vee-validate";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import PhoneNumber from "awesome-phonenumber";
import SlotInvalidInputWatchComponent from "./shared/SlotInvalidInputWatchComponent.vue";

extend("required", {
	...required,
	message: "{_field_} is required.",
});

extend("phone", {
	message() {
		return "Invalid phone number.";
	},
	validate(value) {
		return new Promise((resolve) => {
			// add country code
			if (value.indexOf("+1") < 0) {
				value = "+1" + value;
			}
			var phoneNumber = new PhoneNumber(value);
			resolve({ valid: phoneNumber.isValid() });
		});
	},
});

export default {
	name: "PhoneNumber",
	components: {
		ValidationProvider,
		ValidationObserver,
		SlotInvalidInputWatchComponent,
	},
	data: function () {
		return {
			checked: false,
			submitted: false,
		};
	},
	props: {
		appState: Object,
		lead: Object,
	},
	computed: {
		termsAndConditionsUrl: function () {
			return this.appConfig.termsAndConditionsUrl;
		},
		privacyPolicyUrl: function () {
			return this.appConfig.privacyPolicyUrl;
		},
		partnersUrl: function () {
			return this.appConfig.partnersUrl;
		},
		websiteUrl: function () {
			return this.appConfig.websiteUrl;
		},
		phoneNumberForConsent: function () {
			if (this.validatePhoneNumber(this.lead.phoneNumber)) {
				return this.lead.phoneNumber;
			}

			return "(xxx) xxx-xxxx";
		},
		addressForConsent: function () {
			if (
				this.lead.streetAddress &&
				this.lead.city &&
				this.lead.state &&
				this.lead.zipCode
			) {
				return (
					this.lead.streetAddress +
					", " +
					this.lead.city +
					", " +
					this.lead.state +
					" " +
					this.lead.zipCode
				);
			}

			return "";
		},
		websiteForConsent: function () {
			return document.location.hostname;
		},
	},
	methods: {
		validatePhoneNumber: function (value) {
			if (!value) {
				return false;
			}

			// add country code
			if (value.indexOf("+1") < 0) {
				value = "+1" + value;
			}
			var phoneNumber = new PhoneNumber(value);
			return phoneNumber.isValid();
		},
		enterKeyup: function () {
			this.$refs.complete.click();
		},
		complete: function () {
			this.$emit("complete", this.$options.name, this.lead.phoneNumber);
			this.submitted = true;
		},
		onInvalidInput: function (value) {
			this.utils.log("onInvalidInput: " + value);
			this.$emit("invalidInput", this.$options.name, value);
		},
	},
};
</script>

<style scoped>
#consent-wrapper {
	margin: 0.5rem !important;
}

#leadid_tcpa_disclosure {
	transform: scale(1.5);
}

#tcpa-text {
	text-align: left;

	/* make sure scrollbar doesn't overlap text */
	margin-right: 1rem !important;

	/* fix for tiny checkbox on mobile safari */
	flex: 1;
}

#tcpa-text a {
    color: lightgrey;
    text-decoration: underline;
}

@media (max-width: 1023px) {
	#tcpa-text {
		font-size: 14px;
		line-height: 20px;
		margin-top: 10px;
		margin-right: 0 !important;
	}
}
</style>
